// X12 5010 envelope and segment primitives.
//
// Everything that writes an EDI file for GAMMIS goes through here, because the
// two things that are easy to get wrong are both structural and both reject the
// whole file:
//
//   • SE01 must count the segments from ST through SE inclusive — not the
//     segments in the file, and not the segments pushed so far. The previous
//     generator kept a running counter that had already counted ISA and GS,
//     then added two more, overstating every file by three. Here the count is
//     derived from the array length at the moment the transaction closes, so
//     there is no counter to drift.
//   • The ISA is fixed-width. Every element has an exact length and short
//     values are space-filled, not omitted. A 14-character sender ID is a
//     rejected interchange, not a warning.
//
// Delimiters are the four GAMMIS asks for (837P CG §6.5). They must never
// appear inside data, so `clean()` strips them on the way in rather than
// trusting every call site to remember.

export const ELEMENT    = '*';
export const COMPONENT  = ':';
export const REPETITION = '^';
export const TERMINATOR = '~';

/** GAMMIS's own trading partner ID — ISA08 and GS03 on everything we send. */
export const GAMMIS_TRADING_PARTNER_ID = '77034';

/** Strip delimiters and control characters from a data value. */
export const clean = (v) => String(v ?? '')
  .replace(/[*:~^]/g, ' ')
  .replace(/[\r\n\t]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Fixed-width field: truncate to n, then pad right with spaces. */
export const fixed = (v, n) => clean(v).slice(0, n).padEnd(n, ' ');

/** Zero-padded numeric field, right-aligned, truncated from the left. */
export const numeric = (v, n) => String(v ?? '').replace(/\D/g, '').slice(-n).padStart(n, '0');

/**
 * One segment. Trailing empty elements are dropped — X12 treats a trailing
 * delimiter run as noise and some validators reject it.
 */
export const seg = (id, ...elements) => {
  const parts = elements.map((e) => (e == null ? '' : typeof e === 'object' ? composite(...e) : clean(e)));
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  return [String(id).toUpperCase(), ...parts].join(ELEMENT);
};

/** Composite element — sub-elements joined by the component separator. */
export const composite = (...subs) => {
  const parts = subs.map((s) => (s == null ? '' : clean(s)));
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  return parts.join(COMPONENT);
};

// ── Dates ───────────────────────────────────────────────────────────────────
// All from an ISO date string or a Date. Never from a locale-formatted string:
// the whole file is rejected for one malformed date.

const pad2 = (n) => String(n).padStart(2, '0');

/** CCYYMMDD — GS04, DTP, and every date inside a transaction. */
export const d8 = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10).replace(/-/g, '');
  return `${v.getUTCFullYear()}${pad2(v.getUTCMonth() + 1)}${pad2(v.getUTCDate())}`;
};

/** YYMMDD — ISA09 only. */
export const d6 = (v) => d8(v).slice(2);

/** HHMM — ISA10 and GS05. */
export const t4 = (v) => {
  const dt = v instanceof Date ? v : new Date(v);
  return `${pad2(dt.getUTCHours())}${pad2(dt.getUTCMinutes())}`;
};

// ── Control numbers ─────────────────────────────────────────────────────────
//
// ISA13 must be unique for every interchange a sender ever sends: a receiver
// that sees a number twice rejects the second file as a duplicate (TA1 025).
// Until 2026-10-20 every builder defaulted it to 1 and nothing passed one, so
// every 837P, 270 and 276 — including the three test files Edifecs validated
// in September — carried 000000001. Edifecs does not check for repeats;
// production GAMMIS does, and the second real file would have bounced.
//
// The number now comes from one database counter, `next_edi_control_number()`,
// shared by all three transactions because the rule is per sender, not per
// transaction type. It starts at 2, above the test files. There is no default
// any more: a builder called without a number refuses rather than quietly
// writing 1.

/** The Postgres function that hands out the next number. Called by the app and the SFTP worker. */
export const CONTROL_NUMBER_RPC = 'next_edi_control_number';

/** ISA13 is nine digits. */
export const MAX_CONTROL_NUMBER = 999999999;

/** The control number, or an error saying it has to come from the counter. */
export const requireControlNumber = (n) => {
  const v = typeof n === 'string' && /^\d+$/.test(n.trim()) ? Number(n) : n;
  if (!Number.isInteger(v) || v < 1 || v > MAX_CONTROL_NUMBER) {
    throw new Error(`An X12 file needs a control number from ${CONTROL_NUMBER_RPC}() between 1 and ${MAX_CONTROL_NUMBER}; got ${JSON.stringify(n)}.`);
  }
  return v;
};

// ── Envelope ────────────────────────────────────────────────────────────────

/**
 * Wrap body segments in ST/SE.
 *
 * `body` is every segment between ST and SE. SE01 is body.length + 2 — the two
 * being ST and SE themselves. Derived, not counted.
 */
export const transaction = ({ code, controlNumber, implementationRef, body }) => {
  const ctl = String(controlNumber).padStart(4, '0');
  return [
    seg('ST', code, ctl, implementationRef),
    ...body,
    seg('SE', String(body.length + 2), ctl),
  ];
};

/**
 * Wrap transaction sets in GS/GE. `transactions` is an array of arrays, each
 * one the output of `transaction()`.
 */
export const functionalGroup = ({
  functionalId, senderId, receiverId = GAMMIS_TRADING_PARTNER_ID,
  controlNumber, version, at, transactions,
}) => [
  seg('GS', functionalId, senderId, receiverId, d8(at), t4(at), String(controlNumber), 'X', version),
  ...transactions.flat(),
  seg('GE', String(transactions.length), String(controlNumber)),
];

/**
 * Wrap functional groups in ISA/IEA and render the file.
 *
 * ISA is built by hand rather than through `seg()` because it is the one
 * fixed-width segment in X12 — its elements are positional and space-filled,
 * and `seg()`'s trailing-empty trimming would corrupt it.
 */
export const interchange = ({
  senderId, receiverId = GAMMIS_TRADING_PARTNER_ID,
  controlNumber, usageIndicator, at, groups,
}) => {
  const ctl = numeric(requireControlNumber(controlNumber), 9);
  const isa = [
    'ISA',
    fixed('00', 2), fixed('', 10),
    fixed('00', 2), fixed('', 10),
    fixed('ZZ', 2), fixed(senderId, 15),
    fixed('ZZ', 2), fixed(receiverId, 15),
    d6(at), t4(at),
    REPETITION, '00501', ctl,
    '0', usageIndicator, COMPONENT,
  ].join(ELEMENT);

  return [isa, ...groups.flat(), seg('IEA', String(groups.length), ctl)]
    .map((s) => s + TERMINATOR)
    .join('\n');
};

// ── Reading ─────────────────────────────────────────────────────────────────

/**
 * Split a received file into element arrays, taking the delimiters from its
 * own ISA rather than assuming ours. Inbound 824s, 835s and 271s come back
 * with whatever delimiters the sender used.
 */
export const parse = (text) => {
  const start = String(text || '').indexOf('ISA');
  if (start < 0) return { segments: [], delimiters: null };

  const header = text.slice(start, start + 106);
  const delimiters = {
    element: header[3],
    component: header[104],
    terminator: header[105],
  };

  const segments = text
    .slice(start)
    .split(delimiters.terminator)
    .map((s) => s.replace(/[\r\n]+/g, '').trim())
    .filter(Boolean)
    .map((s) => s.split(delimiters.element));

  return { segments, delimiters };
};

/** First segment with this id, or null. */
export const find = (segments, id) => segments.find((s) => s[0] === id) || null;

/** Every segment with this id. */
export const findAll = (segments, id) => segments.filter((s) => s[0] === id);
